import { Controller } from "@hotwired/stimulus"
import {
  VARIANT_PREVIEW_MODE_KEY_PREFIX,
  GRID_SELECTED_IMAGE_KEY_PREFIX
} from "./variant_preview_constants"

/**
 * Navbar Actions Controller
 * 
 * Manages the active state and visual feedback for navigation links in action bars ONLY.
 * Used exclusively for Projects and Variants action navigation (Info, Search, New, etc.).
 * 
 * Features:
 * - Automatically highlights active links based on current URL
 * - Shows loading spinners on clicked links during Turbo navigation
 * - Handles dropdown menu active states (e.g., "More" dropdown)
 * - Syncs active state on Turbo frame loads and browser navigation
 * - Provides visual feedback with icon swapping and rotation
 * 
 * IMPORTANT: This controller is constrained to action bar contexts only and will NOT
 * interfere with main navigation. It only activates when used within elements that
 * have IDs containing "actions" or classes like "action-bar" or "navbar-actions".
 * 
 * Usage: Add data-controller="navbar-actions" to action bar navigation containers
 * with nav-link and dropdown-item elements (NOT main navigation).
 */
export default class extends Controller {
  connect() {
    this.handleClick = this.handleClick.bind(this)
    this.syncActive = this.syncActive.bind(this)
    this.onFrameLoad = this.onFrameLoad.bind(this)

    // Only add click listener to elements that need spinner functionality
    // Don't interfere with regular navigation links
    this.element.addEventListener('click', this.handleClick, { passive: true })
    document.addEventListener('turbo:load', this.syncActive)
    document.addEventListener('turbo:frame-load', this.onFrameLoad)
    window.addEventListener('popstate', this.syncActive)

    this.syncActive()
    this.updateIconsBasedOnActive()
  }

  disconnect() {
    this.element.removeEventListener('click', this.handleClick)
    document.removeEventListener('turbo:load', this.syncActive)
    document.removeEventListener('turbo:frame-load', this.onFrameLoad)
    window.removeEventListener('popstate', this.syncActive)
  }

  handleClick(event) {
    const link = event.target.closest('a.nav-link, a.dropdown-item')
    if (!link || !this.element.contains(link)) return
    if (link.getAttribute('data-bs-toggle') === 'dropdown') return

    // Don't show spinner for preview mode switches (Spin/Grid buttons)
    if (link.hasAttribute('data-preview-mode')) {
      // Re-sync active state after preview-spin has updated localStorage
      requestAnimationFrame(() => requestAnimationFrame(() => this.syncActive()))
      return
    }

    // Grid mode: Info nav opens per-image Info aside when a thumbnail is selected
    if (link.hasAttribute('data-grid-info-link') && link.dataset.variantUuid) {
      const uuid = link.dataset.variantUuid
      try {
        if (typeof localStorage !== 'undefined' && localStorage.getItem(VARIANT_PREVIEW_MODE_KEY_PREFIX + uuid) === 'grid') {
          const img = localStorage.getItem(GRID_SELECTED_IMAGE_KEY_PREFIX + uuid)
          if (img && window.Turbo?.visit) {
            event.preventDefault()
            const isActionBar = this.element.closest('#actions, [id*="actions"], .action-bar, .navbar-actions, .view-options-nav')
            if (isActionBar) {
              this.lastClickedLink = link
              this.swapIconToSpinner(link)
            }
            window.Turbo.visit(`/products/${uuid}/info?image_id=${encodeURIComponent(img)}`, { frame: 'aside-frame', action: 'advance' })
            return
          }
        }
      } catch (_) {}
    }

    // Only handle clicks on action bar elements - check if this is within an action bar context
    const isActionBar = this.element.closest('#actions, [id*="actions"], .action-bar, .navbar-actions, .view-options-nav')
    if (!isActionBar) {
      // If not in an action bar context, don't interfere at all
      return
    }

    // Don't prevent default behavior - let Turbo handle the navigation
    // this.toggleActive(link) // Removed this line that was interfering with navigation

    // If it's a dropdown item, show spinner on the dropdown toggle instead of the item
    if (link.classList.contains('dropdown-item')) {
      // Find the specific dropdown toggle that contains this dropdown item
      const dropdownContainer = link.closest('.dropdown')
      const dropdownToggle = dropdownContainer ? dropdownContainer.querySelector('a[data-bs-toggle="dropdown"]') : null
      if (dropdownToggle) {
        this.lastClickedDropdownToggle = dropdownToggle
        this.swapIconToSpinner(dropdownToggle)
      }
    } else {
      // Only show spinner on non-dropdown items
      this.lastClickedLink = link
      this.swapIconToSpinner(link)
    }

    // Ensure the default link behavior proceeds (important for Turbo frame navigation)
    // Don't call event.preventDefault() or event.stopPropagation()
  }

  onFrameLoad(e) {
    // When aside updates, re-sync active state and restore icons
    if (!e?.target?.id || e.target.id !== 'aside-frame') return

    if (this.lastClickedLink) {
      this.restoreIcon(this.lastClickedLink)
      this.lastClickedLink = null
    }
    if (this.lastClickedDropdownToggle) {
      this.restoreIcon(this.lastClickedDropdownToggle)
      this.lastClickedDropdownToggle = null
    }
    this.syncActive()
    this.updateIconsBasedOnActive()
  }

  syncActive() {
    // Only sync active state if we're in an action bar context (right-hand nav, view-options/batch nav, etc.)
    const actionBarSelector = '#actions, [id*="actions"], .action-bar, .navbar-actions, .view-options-nav'
    const isActionBar = this.element.closest(actionBarSelector)
    if (!isActionBar) {
      return // Don't sync active state if not in action bar context
    }

    const path = window.location.pathname
    const hasBatchActions = !!this.element.querySelector('.batch-action')
    const activeBatchAction = hasBatchActions ? this.activeBatchActionFromAside() : null

    const links = this.links()
    if (!links.length) return

    const currentPreviewMode = this.getCurrentPreviewMode()

    links.forEach(link => {
      let isActive

      // Spin/Grid: active based on current preview mode (localStorage)
      if (link.hasAttribute('data-preview-mode')) {
        const linkMode = link.getAttribute('data-preview-mode')
        isActive = currentPreviewMode != null && linkMode === currentPreviewMode
      } else if (hasBatchActions && link.classList.contains('batch-action')) {
        // For batch action nav (Move / Clone / Delete), prefer aside content over URL
        if (activeBatchAction) {
          isActive = this.linkMatchesBatchAction(link, activeBatchAction)
        } else {
          isActive = this.linkMatchesPath(link, path)
        }
      } else if (link.dataset.navbarEmbedLink === 'true') {
        isActive = this.variantEmbedNavMatchesPath(link, path)
      } else {
        // Default behavior for regular action bars (Info, Filter, etc.)
        isActive = this.linkMatchesPath(link, path)
      }

      link.classList.toggle('active', isActive)
    })

    // Handle dropdown toggle active state
    this.updateDropdownToggleActive(path)
    this.updateIconsBasedOnActive()
  }

  // Read the current batch action from the aside content (if any)
  activeBatchActionFromAside() {
    const aside = document.getElementById('aside-frame')
    if (!aside) return null
    const el = aside.querySelector('[data-batch-action]')
    return el ? el.getAttribute('data-batch-action') : null
  }

  // Match a link against a batch action name ("move", "clone", "delete")
  linkMatchesBatchAction(link, action) {
    try {
      const href = link.getAttribute('href') || '#'
      const url = new URL(href, window.location.origin)
      const hrefPath = url.pathname

      if (action === 'delete') {
        // Delete uses a collection route like /projects/delete
        return hrefPath.includes('/projects/delete')
      }

      // Move / Clone are member routes: /projects/:id/move or /projects/:id/clone
      return hrefPath.endsWith('/' + action)
    } catch (_) {
      return false
    }
  }

  toggleActive(activeLink) {
    this.links().forEach(link => link.classList.remove('active'))
    activeLink.classList.add('active')
    this.updateIconsBasedOnActive()
  }

  links() {
    // Only return links that are within action bar context
    const actionBarSelector = '#actions, [id*="actions"], .action-bar, .navbar-actions, .view-options-nav'
    const isActionBar = this.element.closest(actionBarSelector)
    if (!isActionBar) {
      return [] // Don't return any links if not in action bar context
    }
    return Array.from(this.element.querySelectorAll('a.nav-link, a.dropdown-item'))
  }

  swapIconToSpinner(link) {
    const icon = link.querySelector('i.bi')
    if (!icon) return
    if (!link.dataset.originalIconHtml) {
      link.dataset.originalIconHtml = icon.outerHTML
    }
    // Keep existing sizing classes, just swap glyph to arrow-repeat and add rotation
    const classes = icon.className.split(/\s+/)
    const newClasses = classes.map(c => (c.startsWith('bi-') && c !== 'bi-lg') ? 'bi-arrow-repeat' : c)
    if (!newClasses.includes('bi-arrow-repeat')) newClasses.push('bi-arrow-repeat')
    if (!newClasses.includes('rotate-clockwise')) newClasses.push('rotate-clockwise')
    if (!newClasses.includes('bi')) newClasses.unshift('bi')
    icon.className = newClasses.join(' ')
  }

  restoreIcon(link) {
    try {
      if (!link?.dataset?.originalIconHtml) return
      const currentIcon = link.querySelector('i.bi')
      if (currentIcon) {
        currentIcon.outerHTML = link.dataset.originalIconHtml
      }
    } finally {
      delete link.dataset.originalIconHtml
    }
  }

  updateDropdownToggleActive(currentPath) {
    // Find dropdown toggle within this specific controller element
    const dropdownToggle = this.element.querySelector('a[data-bs-toggle="dropdown"]')
    if (!dropdownToggle) return

    // Check if any dropdown items within this specific dropdown are active
    const dropdownContainer = dropdownToggle.closest('.dropdown')
    const dropdownItems = dropdownContainer ? dropdownContainer.querySelectorAll('.dropdown-item') : []
    const hasActiveDropdownItem = Array.from(dropdownItems).some(item => {
      return this.linkMatchesPath(item, currentPath)
    })

    // Set dropdown toggle as active if any dropdown item is active
    dropdownToggle.classList.toggle('active', hasActiveDropdownItem)
  }

  updateIconsBasedOnActive() {
    // Only update icons if we're in an action bar context
    const isActionBar = this.element.closest('#actions, [id*="actions"], .action-bar, .navbar-actions')
    if (!isActionBar) {
      return // Don't update icons if not in action bar context
    }

    const links = this.links()
    links.forEach(link => {
      const isActive = link.classList.contains('active')
      const icon = link.querySelector('i.bi')
      if (!icon) return

      const outlineGlyph = icon.getAttribute('data-icon-outline')
      const filledGlyph = icon.getAttribute('data-icon-filled')
      if (!outlineGlyph && !filledGlyph) return

      const desiredGlyph = isActive ? (filledGlyph || outlineGlyph) : (outlineGlyph || filledGlyph)
      if (!desiredGlyph) return

      const classes = icon.className.split(/\s+/)
      const keptClasses = classes.filter(c => !c.startsWith('bi-') || c === 'bi-lg')
      // Ensure base 'bi' class remains
      const newClasses = ['bi', ...keptClasses]
      if (!newClasses.includes('bi-lg') && classes.includes('bi-lg')) newClasses.push('bi-lg')
      // Preserve margin utility classes like me-1, me-2, etc.
      classes.forEach(c => { if (/^(m[stebxy]?|p[stebxy]?)-\d$/.test(c)) newClasses.push(c) })
      // Add desired glyph
      newClasses.push(desiredGlyph)
      icon.className = Array.from(new Set(newClasses)).join(' ')
    })
  }

  // Current preview mode for variant pages (Spin vs Grid). Reads from localStorage.
  // Returns "360" | "grid" | null (null when not on a variant product page).
  getCurrentPreviewMode() {
    const path = window.location.pathname
    const match = path.match(/\/products\/([^/]+)/)
    if (!match) return null
    const variantUuid = match[1]
    const key = VARIANT_PREVIEW_MODE_KEY_PREFIX + variantUuid
    try {
      const mode = typeof localStorage !== "undefined" && localStorage.getItem(key)
      if (mode === "grid" || mode === "360") return mode
      return "360"
    } catch (_) {
      return "360"
    }
  }

  /** Embed + per-image embed share the same nav item; highlight on /embed and /embed_image */
  variantEmbedNavMatchesPath(link, currentPath) {
    try {
      const href = link.getAttribute('href') || ''
      const url = new URL(href, window.location.origin)
      if (url.origin !== window.location.origin) return false
      const base = url.pathname
      const m = base.match(/^(\/products\/[^/]+)\/embed$/)
      if (!m) return false
      const prefix = m[1]
      return currentPath === `${prefix}/embed` || currentPath === `${prefix}/embed_image`
    } catch (_) {
      return false
    }
  }

  linkMatchesPath(link, currentPath) {
    try {
      const href = link.getAttribute('href') || '#'
      const url = new URL(href, window.location.origin)
      // Never mark links to a different origin active
      if (url.origin !== window.location.origin) return false
      const hrefPath = url.pathname
      const strategy = (this.element.dataset.activeStrategy || 'exact').toLowerCase()

      // Special handling for dropdown items - check if current action matches dropdown action
      if (link.classList.contains('dropdown-item')) {
        const pathParts = currentPath.split('/')
        const lastPart = pathParts[pathParts.length - 1]
        const hrefParts = hrefPath.split('/')
        const hrefLastPart = hrefParts[hrefParts.length - 1]
        return lastPart === hrefLastPart
      }

      if (strategy === 'prefix') {
        // Allow explicit prefixes per-link via data-active-prefixes="/a,/b"
        const prefixesAttr = link.getAttribute('data-active-prefixes')
        if (prefixesAttr) {
          const prefixes = prefixesAttr.split(',').map(p => p.trim()).filter(Boolean)
          for (const prefix of prefixes) {
            if (prefix === '/') {
              if (currentPath === '/') return true
            } else if (currentPath === prefix || currentPath.startsWith(prefix + '/')) {
              return true
            }
          }
          return false
        }
        if (hrefPath === '/') return currentPath === '/'
        return currentPath === hrefPath || currentPath.startsWith(hrefPath + '/')
      }

      // exact match (default)
      return currentPath === hrefPath
    } catch (_) {
      return false
    }
  }
}